// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import find from 'lodash/find';
import filter from 'lodash/filter';
import each from 'lodash/each';
import keys from 'lodash/keys';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Button, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { getI18n } from "../../../utils/intl";

import Filter from '../Filter';

import {
  FILTER_ENTITY_TYPE_API,
  FILTER_ENTITY_TYPE_APP,
  FILTER_ENTITY_TYPE_ORG,
  FILTER_TYPE_RANK,
  FILTER_TYPE_NAME,
} from '../../../constants/filter';
import { getFilterTitle, encodeMetricsFilter, getSelectedFilters } from '../utils/filter';

import styles from './styles';

const defaultFilterData = {
  filterType: FILTER_TYPE_RANK,
  rank: 1,
  selectedItems: {},
};

const MAX_ALLOWED_SLOTS = 10;

const getFilterName = (key) => {
  let name = '';
  switch (key) {
    case FILTER_ENTITY_TYPE_API:
      name = 'APIs';
      break;
    case FILTER_ENTITY_TYPE_APP:
      name = 'Apps';
      break;
    case FILTER_ENTITY_TYPE_ORG:
      name = 'Orgs';
      break;
    default: return '';
  }
  return name;
}

const FilterWrapper = (props) => {
  const {
    selectedMetrics,
    handleFiltersChange: handleFiltersChangeProp,
    updateMetricFilters,
    isDeveloper,
    isOrgAdmin,
    hideOrgFilterForOrgPub,
    apiList,
    appList,
    orgList,
    handleSearchFilterNameChange,
    onEntityTypeChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  
  const [shouldShowFilterPopUp, setShouldShowFilterPopUp] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterInEditMode, setIsFilterInEditMode] = useState(false);
  const [popUpFilterEntityType, setPopUpFilterEntityType] = useState('');
  const [editFilterPopupId, setEditFilterPopupId] = useState(null);
  const [selectedFiltersState, setSelectedFiltersState] = useState([
    {
      ...defaultFilterData,
      entityType: FILTER_ENTITY_TYPE_API,
      rank: 3,
    },
  ]);

  useEffect(() => {
    if (!isUndefined(selectedMetrics)) {
      setSelectedFiltersState(selectedMetrics);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('mousedown', handleClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);
 
  const getMaxSlotSize = (entityType) => {
    let otherPermutations = 1;
    each(selectedFiltersState, filterState => {
      if (filterState.entityType !== entityType) {
        if (filterState.filterType === FILTER_TYPE_NAME) {
          // shouldn't return 0, but handling it as a precaution
          otherPermutations *= (keys(filterState.selectedItems).length || 1);
        } else if (filterState.filterType === FILTER_TYPE_RANK) {
          // shouldn't return 0, but handling it as a precaution
          otherPermutations *= (filterState.rank || 1);
        }
      }
    });
    return Math.floor(MAX_ALLOWED_SLOTS / otherPermutations);
  };

  const getCollapsedFilterLabel = selectedFilterState => {
    const name = getFilterName(selectedFilterState.entityType);
    if (selectedFilterState.filterType === FILTER_TYPE_NAME) {
      const itemCount = selectedFilterState.selectedItemsCount;
      return intl.getI18nMessage(
        'label.analytics.filter.filterByName.collapsedFilterLabel.text',
        { itemCount, name },
      );
    } else if (selectedFilterState.filterType === FILTER_TYPE_RANK) {
      const rank = selectedFilterState.rank;
      return intl.getI18nMessage(
        'label.analytics.filter.filterByRank.collapsedFilterLabel.text',
        { rank, name },
      );
    }
    return '';
  };

  const getFilterDropdownButtonClassname = (entityType, classes) => (
    selectedFiltersState.filter(
      filterSelected => filterSelected.entityType === entityType,
    ).length
      ? classes.filterTypeBtn__disabled
      : null
  );

  const handleKeyDown = (event) => {
    if (event.keyCode === 27) {
      setIsDropdownOpen(false);
    }
  };

  const handleClick = (event) => {
    if (event.which === 1) {
      const targetElement = event.target;
      const filterContainer = targetElement.closest('#filter-container');
      const filterBtn = targetElement.closest('#filter-btn');
      if (isNull(filterContainer) && isNull(filterBtn)) {
        setIsDropdownOpen(false);
      }
    }
  };

  const showFilterPopUp = (type) => {
    setIsDropdownOpen(false);
    setPopUpFilterEntityType(type);
    onEntityTypeChange(type);
    setShouldShowFilterPopUp(value => !value);
  };

  const removeFilterPopUp = () => {
    setIsFilterInEditMode(false);
    setShouldShowFilterPopUp(false);
  };

  const toggleAddFilterDropdown = () => {
    setIsDropdownOpen(value => !value);
    setShouldShowFilterPopUp(false);
    setIsFilterInEditMode(false);
  };

  const editFilter = (key) => {
    setEditFilterPopupId(key);
    setIsFilterInEditMode(value => !value);
    showFilterPopUp(key);
  }

  const removeFilter = (key) => {
    const updatedSelectedFilters = filter(selectedFiltersState,
      item => item.entityType !== key);
    setSelectedFiltersState(updatedSelectedFilters);
    handleFiltersChange(updatedSelectedFilters);
  }

  const handleFiltersChange = (updatedFiltersState) => {
    const selectedFilters = getSelectedFilters(updatedFiltersState) || {};
    handleFiltersChangeProp(selectedFilters);
    updateMetricFilters(encodeMetricsFilter(updatedFiltersState));
  }

  const handleFilterChange = (filterState) => {
    const prevSelectedFiltersState = cloneDeep(selectedFiltersState);
    const index = prevSelectedFiltersState.findIndex(
      item => item.entityType === filterState.entityType);
    let updatedFilterState = {};
    if (index !== -1) {
      updatedFilterState = prevSelectedFiltersState[index];
    } else {
      updatedFilterState.entityType = filterState.entityType;
    }
    updatedFilterState.filterType = filterState.filterType;
    if (filterState.filterType === FILTER_TYPE_RANK) {
      updatedFilterState.rank = filterState.rank;
    } else if (filterState.filterType === FILTER_TYPE_NAME) {
      updatedFilterState.selectedItems = filterState.selectedItems;
      updatedFilterState.selectedItemsCount = filterState.selectedItemsCount;
    }
    if (index !== -1) {
      prevSelectedFiltersState[index] = updatedFilterState;
    } else {
      prevSelectedFiltersState.push(updatedFilterState);
    }
    setSelectedFiltersState(prevSelectedFiltersState);
    removeFilterPopUp();
    handleFiltersChange(prevSelectedFiltersState);
  };

  const isFilterButtonDisabled = (classes) => {
    const containsAppFilter = selectedFiltersState.filter(
      filterSelected => filterSelected.entityType === FILTER_ENTITY_TYPE_APP,
    ).length;
    if (selectedFiltersState.length < 3 && containsAppFilter) {
      return classes.filterTypeBtn__disabled;
    }
    return null;
  }

  const renderAddFilterDropdown = (classes) => (
    isDropdownOpen ? (
      <div
        className={classes.addFilterDropdown}
        data-apim-test={'add-filter-dropdown'}
      >
        <Button
          className={clsx(
            classes.filterTypeBtn,
            getFilterDropdownButtonClassname(
              FILTER_ENTITY_TYPE_API,
              classes,
            ),
          )}
          onClick={() => showFilterPopUp(FILTER_ENTITY_TYPE_API)}
        >
          {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.add.api')}
        </Button>
        <Button
          className={clsx(
            classes.filterTypeBtn,
            getFilterDropdownButtonClassname(
              FILTER_ENTITY_TYPE_APP,
              classes,
            ),
          )}
          onClick={() => showFilterPopUp(FILTER_ENTITY_TYPE_APP)}
        >
          {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.add.application')}
        </Button>
        {(!isDeveloper && !isOrgAdmin
        && !hideOrgFilterForOrgPub) &&
          <Button
            className={clsx(
              classes.filterTypeBtn,
              getFilterDropdownButtonClassname(
                FILTER_ENTITY_TYPE_ORG,
                classes,
              ),
              isFilterButtonDisabled(classes),
            )}
            onClick={() => showFilterPopUp(FILTER_ENTITY_TYPE_ORG)}
          >
            {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.add.organization')}
          </Button>
        }
      </div>
  ) : null);

  const renderFilterPopup = () => {
    const title = getFilterTitle(popUpFilterEntityType, intl);
    let nameMap = {};
    if (popUpFilterEntityType === FILTER_ENTITY_TYPE_API) {
      nameMap = apiList;
    } else if (popUpFilterEntityType === FILTER_ENTITY_TYPE_APP) {
      nameMap = appList;
    } else if (popUpFilterEntityType === FILTER_ENTITY_TYPE_ORG) {
      nameMap = orgList;
    }
    const selectedFilterState = find(selectedFiltersState,
      filterState => filterState.entityType === popUpFilterEntityType) || defaultFilterData;
    return (<Filter
      closeFilter={removeFilterPopUp}
      entityNameMap={nameMap}
      entityType={popUpFilterEntityType}
      title={title}
      filterInitState={{ ...selectedFilterState }}
      showFilter={shouldShowFilterPopUp}
      handleFilterChange={handleFilterChange}
      maxAllowedEntities={getMaxSlotSize(popUpFilterEntityType)}
      handleSearchFilterNameChange={handleSearchFilterNameChange}
    />);
  };

  return (
    <div className={classes.filterWrapper} id={'filter-btn'}>
      <div className={classes.filterTitle}>
        {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.title')}
      </div>
      <Button
        className={classes.addFilterBtn}
        data-apim-test={'add-filter-button'}
        onClick={toggleAddFilterDropdown}
        endIcon={
          <ArrowDropDownIcon
            fontSize='small'
            className={clsx(classes.icon, { [classes.dropdownOpen]: isDropdownOpen })}
          />
        }
      >
        {intl.getI18nMessage('label.analyticsDashboard.filter.dropdown.add.label')}
      </Button>
      {selectedFiltersState &&
        selectedFiltersState.map(value => (
          <div className={classes.editFilterContainer} id={'filter-container'} key={value.entityType}>
            <Button
              className={classes.popupBtn}
              onClick={() => editFilter(value.entityType)}
            >
              {getCollapsedFilterLabel(value)}
            </Button>
            <Button
              className={classes.closeButton}
              data-apim-test={'selected-filter-close-button'}
              onClick={() => removeFilter(value.entityType)}
              startIcon={<CloseIcon className={classes.icon} fontSize='small'/>}
            />
            {isFilterInEditMode &&
              editFilterPopupId === value.entityType &&
              renderFilterPopup()}
          </div>
        ))}
      {renderAddFilterDropdown(classes)}
      {!isFilterInEditMode && shouldShowFilterPopUp && renderFilterPopup()}
    </div>
  );
}

FilterWrapper.propTypes = {
  apiList: PropTypes.array,
  appList: PropTypes.array,
  orgList: PropTypes.array,
  handleFiltersChange: PropTypes.func,
  updateMetricFilters: PropTypes.func,
  selectedMetrics: PropTypes.arrayOf(PropTypes.object),
  isDeveloper: PropTypes.bool,
  isOrgAdmin: PropTypes.bool,
  hideOrgFilterForOrgPub: PropTypes.bool,
  handleSearchFilterNameChange: PropTypes.func,
  onEntityTypeChange: PropTypes.func,
}

export default FilterWrapper;